import { useState, useMemo } from 'react'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { formatDistance, getStorePhotoUrl } from '@/utils/formatters'
import { MapPinIcon, WalkingIcon, ClockIcon } from '@/components/ui/icons'
import { getStorePickupStatus } from '@/utils/storePickupStatus'
import type { Store } from '@/types'

interface MobileStoreCardProps {
  store: Store
  isSelected: boolean
  onSelect: () => void
  onFocus?: () => void
}

export function MobileStoreCard({ store, isSelected, onSelect, onFocus }: MobileStoreCardProps) {
  const photoUrl = getStorePhotoUrl(store.storePhotoPath, store.logoPath)
  const [imgError, setImgError] = useState(false)
  const pickupStatus = useMemo(() => getStorePickupStatus(store), [store])

  return (
    <button
      onClick={onSelect}
      onFocus={onFocus}
      role="option"
      aria-selected={isSelected}
      className={`
        shrink-0 w-[280px] rounded-2xl overflow-hidden
        bg-surface-elevated
        border-2 transition-all duration-200
        snap-start
        ${
          isSelected
            ? 'border-brand-primary shadow-elevated scale-[1.02]'
            : 'border-transparent shadow-card hover:shadow-card-hover'
        }
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary
        active:scale-[0.98]
      `}
    >
      <div className="flex">
        <div className="w-20 shrink-0 bg-surface-tertiary">
          {photoUrl && !imgError ? (
            <img
              src={photoUrl}
              alt={`${store.name} location`}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <MapPinIcon className="size-6 text-text-tertiary" />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 p-3">
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-text-primary truncate">
                {store.name}
              </h3>
              <p className="text-xs text-text-secondary truncate mt-0.5">
                {store.street}, {store.area}
              </p>
            </div>
            <StatusBadge status={pickupStatus.status} size="sm" />
          </div>

          <div className="flex items-center gap-3 text-sm/5 text-text-secondary">
            {store.distance !== undefined && (
              <span className="flex items-center gap-1">
                <MapPinIcon className="size-3.5 text-text-tertiary shrink-0" />
                {formatDistance(store.distance)}
              </span>
            )}
            {store.travelTime !== undefined && (
              <span className="flex items-center gap-1">
                <WalkingIcon className="size-3.5 text-text-tertiary shrink-0" />
                {store.travelTime} min
              </span>
            )}
            {pickupStatus.closingTime && (
              <span className="flex items-center gap-1 ml-auto">
                <ClockIcon className="size-3.5 text-text-tertiary shrink-0" />
                {pickupStatus.status === 'closing_soon'
                  ? `Closes ${pickupStatus.closingTime}`
                  : `Till ${pickupStatus.closingTime}`}
              </span>
            )}
          </div>

          {!pickupStatus.isAvailableForPickup && pickupStatus.nextOpenTime && (
            <p className="mt-1 text-xs text-text-tertiary">
              {pickupStatus.nextOpenTime}
            </p>
          )}
        </div>
      </div>
    </button>
  )
}
